import React from "react";
import {
  Globe,
  Edit3,
  X,
  Save,
  RefreshCw,
} from "lucide-react";
import Button from "../../../../components/common/Button";
import { Input } from "../../../../components/common/Input";
import { SocialUrlData } from "./types";

export interface SocialUrlsTabProps {
  socialData: SocialUrlData | null;
  loadingSocial: boolean;
  savingSocial: boolean;
  isEditingSocial: boolean;
  setIsEditingSocial: (editing: boolean) => void;
  facebookUrl: string;
  setFacebookUrl: (val: string) => void;
  instagramUrl: string;
  setInstagramUrl: (val: string) => void;
  twitterUrl: string;
  setTwitterUrl: (val: string) => void;
  linkedinUrl: string;
  setLinkedinUrl: (val: string) => void;
  githubUrl: string;
  setGithubUrl: (val: string) => void;
  youtubeUrl: string;
  setYoutubeUrl: (val: string) => void;
  whatsappUrl: string;
  setWhatsappUrl: (val: string) => void;
  handleSaveSocialUrls: (e: React.FormEvent) => void;
}

export const SocialUrlsTab: React.FC<SocialUrlsTabProps> = ({
  socialData,
  loadingSocial,
  savingSocial,
  isEditingSocial,
  setIsEditingSocial,
  facebookUrl,
  setFacebookUrl,
  instagramUrl,
  setInstagramUrl,
  twitterUrl,
  setTwitterUrl,
  linkedinUrl,
  setLinkedinUrl,
  githubUrl,
  setGithubUrl,
  youtubeUrl,
  setYoutubeUrl,
  whatsappUrl,
  setWhatsappUrl,
  handleSaveSocialUrls,
}) => {
  return (
    <div className="cht-space-y-6">
      <div className="cht-flex cht-items-center cht-justify-between">
        <div>
          <h3 className="cht-text-lg cht-font-black cht-text-[#1e293b]">Social Media Connections</h3>
          <p className="cht-text-xs cht-text-gray-500">Manage corporate social media links rendered across website headers, footers, and correspondence.</p>
        </div>
        {!isEditingSocial ? (
          <Button
            variant="primary"
            size="sm"
            onClick={() => setIsEditingSocial(true)}
            leftIcon={<Edit3 className="cht-h-4 cht-w-4" />}
            className="cht-rounded-xl cht-font-bold cht-text-xs"
          >
            Edit Social Links
          </Button>
        ) : (
          <div className="cht-flex cht-items-center cht-space-x-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setIsEditingSocial(false)}
              leftIcon={<X className="cht-h-3.5 cht-w-3.5" />}
              className="cht-rounded-xl cht-font-bold cht-text-xs"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              form="social-urls-edit-form"
              variant="primary"
              size="sm"
              disabled={savingSocial}
              isLoading={savingSocial}
              leftIcon={<Save className="cht-h-4 cht-w-4" />}
              className="cht-rounded-xl cht-font-bold cht-text-xs cht-min-w-[130px]"
            >
              {savingSocial ? "Saving..." : "Save Links"}
            </Button>
          </div>
        )}
      </div>

      <div className="cht-bg-white cht-border cht-border-gray-100 cht-shadow-sm cht-rounded-[24px] cht-p-6">
        {loadingSocial ? (
          <div className="cht-py-12 cht-text-center cht-text-xs cht-text-gray-500">
            <RefreshCw className="cht-h-5 cht-w-5 cht-text-[#3e57da] cht-animate-spin cht-mx-auto cht-mb-2" />
            Loading corporate social media URLs...
          </div>
        ) : (
          <form id="social-urls-edit-form" onSubmit={handleSaveSocialUrls} className="cht-space-y-5 cht-text-xs">
            <div className="cht-grid cht-grid-cols-1 md:cht-grid-cols-2 cht-gap-4">
              <div>
                <label className="cht-font-bold cht-text-gray-700 cht-block cht-mb-1">Facebook URL</label>
                {!isEditingSocial ? (
                  <div className="cht-p-3.5 cht-bg-gray-50/80 cht-rounded-xl cht-border cht-border-gray-100 cht-font-mono cht-text-gray-800 cht-text-xs cht-truncate">
                    {socialData?.facebook_url || "Not set"}
                  </div>
                ) : (
                  <Input
                    type="url"
                    value={facebookUrl}
                    onChange={(e) => setFacebookUrl(e.target.value)}
                    placeholder="https://facebook.com/codehubtech"
                    leftIcon={<Globe className="cht-h-4 cht-w-4 cht-text-gray-400" />}
                    inputClassName="cht-font-mono"
                  />
                )}
              </div>

              <div>
                <label className="cht-font-bold cht-text-gray-700 cht-block cht-mb-1">Instagram URL</label>
                {!isEditingSocial ? (
                  <div className="cht-p-3.5 cht-bg-gray-50/80 cht-rounded-xl cht-border cht-border-gray-100 cht-font-mono cht-text-gray-800 cht-text-xs cht-truncate">
                    {socialData?.instagram_url || "Not set"}
                  </div>
                ) : (
                  <Input
                    type="url"
                    value={instagramUrl}
                    onChange={(e) => setInstagramUrl(e.target.value)}
                    placeholder="https://instagram.com/codehubtech"
                    leftIcon={<Globe className="cht-h-4 cht-w-4 cht-text-gray-400" />}
                    inputClassName="cht-font-mono"
                  />
                )}
              </div>

              <div>
                <label className="cht-font-bold cht-text-gray-700 cht-block cht-mb-1">Twitter / X URL</label>
                {!isEditingSocial ? (
                  <div className="cht-p-3.5 cht-bg-gray-50/80 cht-rounded-xl cht-border cht-border-gray-100 cht-font-mono cht-text-gray-800 cht-text-xs cht-truncate">
                    {socialData?.twitter_url || "Not set"}
                  </div>
                ) : (
                  <Input
                    type="url"
                    value={twitterUrl}
                    onChange={(e) => setTwitterUrl(e.target.value)}
                    placeholder="https://x.com/codehubtech"
                    leftIcon={<Globe className="cht-h-4 cht-w-4 cht-text-gray-400" />}
                    inputClassName="cht-font-mono"
                  />
                )}
              </div>

              <div>
                <label className="cht-font-bold cht-text-gray-700 cht-block cht-mb-1">LinkedIn URL</label>
                {!isEditingSocial ? (
                  <div className="cht-p-3.5 cht-bg-gray-50/80 cht-rounded-xl cht-border cht-border-gray-100 cht-font-mono cht-text-gray-800 cht-text-xs cht-truncate">
                    {socialData?.linkedin_url || "Not set"}
                  </div>
                ) : (
                  <Input
                    type="url"
                    value={linkedinUrl}
                    onChange={(e) => setLinkedinUrl(e.target.value)}
                    placeholder="https://linkedin.com/company/codehubtech"
                    leftIcon={<Globe className="cht-h-4 cht-w-4 cht-text-gray-400" />}
                    inputClassName="cht-font-mono"
                  />
                )}
              </div>

              <div>
                <label className="cht-font-bold cht-text-gray-700 cht-block cht-mb-1">GitHub URL</label>
                {!isEditingSocial ? (
                  <div className="cht-p-3.5 cht-bg-gray-50/80 cht-rounded-xl cht-border cht-border-gray-100 cht-font-mono cht-text-gray-800 cht-text-xs cht-truncate">
                    {socialData?.github_url || "Not set"}
                  </div>
                ) : (
                  <Input
                    type="url"
                    value={githubUrl}
                    onChange={(e) => setGithubUrl(e.target.value)}
                    placeholder="https://github.com/codehub-technologies"
                    leftIcon={<Globe className="cht-h-4 cht-w-4 cht-text-gray-400" />}
                    inputClassName="cht-font-mono"
                  />
                )}
              </div>

              <div>
                <label className="cht-font-bold cht-text-gray-700 cht-block cht-mb-1">YouTube URL</label>
                {!isEditingSocial ? (
                  <div className="cht-p-3.5 cht-bg-gray-50/80 cht-rounded-xl cht-border cht-border-gray-100 cht-font-mono cht-text-gray-800 cht-text-xs cht-truncate">
                    {socialData?.youtube_url || "Not set"}
                  </div>
                ) : (
                  <Input
                    type="url"
                    value={youtubeUrl}
                    onChange={(e) => setYoutubeUrl(e.target.value)}
                    placeholder="https://youtube.com/@codehubtech"
                    leftIcon={<Globe className="cht-h-4 cht-w-4 cht-text-gray-400" />}
                    inputClassName="cht-font-mono"
                  />
                )}
              </div>

              <div>
                <label className="cht-font-bold cht-text-gray-700 cht-block cht-mb-1">WhatsApp Channel / Line</label>
                {!isEditingSocial ? (
                  <div className="cht-p-3.5 cht-bg-gray-50/80 cht-rounded-xl cht-border cht-border-gray-100 cht-font-mono cht-text-gray-800 cht-text-xs cht-truncate">
                    {socialData?.whatsapp_url || "Not set"}
                  </div>
                ) : (
                  <Input
                    type="text"
                    value={whatsappUrl}
                    onChange={(e) => setWhatsappUrl(e.target.value)}
                    placeholder="https://wa.me/2348000000000"
                    leftIcon={<Globe className="cht-h-4 cht-w-4 cht-text-gray-400" />}
                    inputClassName="cht-font-mono"
                  />
                )}
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};
